/**
 * Athena Workspace — Recipe Showcase Renderer (Phase 2.1 — polish 10/10)
 *
 * Renderiza a apresentação estruturada de receita gerada pelo BACKEND
 * (campo `workspace_presentation` do documento da mensagem, layout === 'recipe').
 * Zero parsing de markdown aqui: o backend já entrega JSON pronto.
 *
 * Uso:
 *   <RecipeShowcaseRenderer data={message.workspace_presentation} onAction={id => ...} />
 *
 * Estilos 100% inline (sem Tailwind) — componente autocontido.
 */

import React from 'react';
import { ProseText } from './ProseText';

// ── Paleta ────────────────────────────────────────────────────────────────────
const R = {
  card: '#0c1220', border: '#1a2240',
  accent: '#3B82F6', accentSoft: 'rgba(59,130,246,0.12)', accentBdr: 'rgba(59,130,246,0.22)',
  text: '#e8eaf2', textSec: 'rgba(220,234,255,0.78)', textMut: 'rgba(150,170,220,0.6)',
  green: '#22c55e', greenSoft: 'rgba(34,197,94,0.12)', greenBdr: 'rgba(34,197,94,0.22)',
  indigo: '#818cf8', indigoBdr: 'rgba(99,102,241,0.3)',
  amber: '#f59e0b', amberSoft: 'rgba(245,158,11,0.08)', amberBdr: 'rgba(245,158,11,0.25)',
  purple: '#a78bfa', purpleBdr: 'rgba(139,92,246,0.22)',
  purpleSolid: 'rgba(139,92,246,0.85)',
  cardHover: 'rgba(255,255,255,0.025)',
  chipHover: 'rgba(59,130,246,0.08)',
  chipHoverBdr: 'rgba(59,130,246,0.35)',
  greenHover: 'rgba(34,197,94,0.08)',
  actionHover: 'rgba(255,255,255,0.05)',
  heroGrad: 'linear-gradient(135deg, #0b1524 0%, #0d1a2e 100%)',
  tipGrad: 'linear-gradient(135deg, rgba(245,158,11,0.13) 0%, rgba(245,158,11,0.05) 100%)',
};

// Base compartilhada por todos os cards — raio 14 unifica a linguagem visual
const _rCard = {
  background: R.card,
  border: `1px solid ${R.border}`,
  borderRadius: 14,
  padding: '14px 18px',
  boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
};

const sectionLabel = {
  display: 'flex', alignItems: 'center', gap: 7,
  fontSize: 11, fontWeight: 800, color: R.textMut,
  textTransform: 'uppercase', letterSpacing: '0.09em', marginBottom: 12,
};

// ── Fallbacks inteligentes: nenhum campo mostra "—" ──────────────────────────
function deriveMeta(data) {
  const d = data || {};

  // time: rejeita se >25 chars ou sem dígito
  const t = d.time || '';
  const recipeTime = (t.length < 25 && /\d/.test(t)) ? t : '~30 min';

  // difficulty: normaliza para conjunto conhecido
  const rawDiff = (d.difficulty || '').toLowerCase().trim();
  const recipeDiff = /fácil|facil|easy|simples/.test(rawDiff) ? 'Fácil'
    : /médio|medio|medium|modera/.test(rawDiff) ? 'Médio'
    : /difícil|dificil|hard/.test(rawDiff) ? 'Difícil'
    : 'Fácil';

  // servings: rejeita se sem dígito
  const s = d.servings || '';
  const recipeServings = /\d/.test(s) ? s : '4 porções';

  // type: rejeita valores de perfil ("Equilibrado" etc.) — sempre categoria de prato
  const profileWords = /equilibr|fitness|vegeta|indulg|leve|saudá|sauda|prático|pratico/i;
  const rawType = d.type || d.dish_type || '';
  const recipeType = (rawType && !profileWords.test(rawType) && rawType.length < 30)
    ? rawType : 'Prato principal';

  // profile: o perfil nutricional/estilo
  const rawProfile = d.profile || d.dish_type || '';
  const recipeProfile = rawProfile || 'Equilibrado';

  return { recipeTime, recipeDiff, recipeServings, recipeType, recipeProfile };
}

// ── Descrição auto-gerada quando o backend não manda uma válida ──────────────
function autoDesc(name) {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (/frango|galinha/.test(lower)) return 'Macio por dentro, dourado por fora e cheio de sabor.';
  if (/carne|bife|picanha/.test(lower)) return 'Suculento, aromático e perfeito para qualquer ocasião.';
  if (/peixe|salmão|tilapia|atum/.test(lower)) return 'Leve, saboroso e rico em proteínas.';
  if (/macarrão|pasta|espaguete/.test(lower)) return 'Al dente, com molho encorpado e cheio de personalidade.';
  if (/bolo|torta|sobremesa/.test(lower)) return 'Macio, aromático e irresistível.';
  if (/salada/.test(lower)) return 'Fresca, colorida e nutritiva para o dia a dia.';
  if (/sopa|caldo/.test(lower)) return 'Reconfortante, saborosa e nutritiva.';
  return 'Uma receita deliciosa para surpreender em qualquer ocasião.';
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = React.useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < bp : false)
  );
  React.useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return mobile;
}

function Label({ icon, label }) {
  return (
    <div style={sectionLabel}>
      <span style={{ fontSize: 13 }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

// ── Prosa da Athena (intro) — texto puro, sem card ────────────────────────────
function ProseBlock({ text }) {
  if (!text) return null;
  return (
    <ProseText
      text={text}
      as="div"
      style={{ fontSize: 13, color: R.textSec, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
    />
  );
}

// ── After-text: bolha conversacional da Athena (ícone + prosa) ────────────────
// A última linha com "?" ganha destaque (CTA conversacional).
function AfterTextBubble({ text }) {
  if (!text) return null;
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{
        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
        background: 'radial-gradient(circle at 35% 35%, rgba(139,92,246,0.85), rgba(59,130,246,0.65))',
        border: '1px solid rgba(139,92,246,0.28)',
        boxShadow: '0 2px 10px rgba(139,92,246,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13,
      }}>🌀</div>
      <div style={{
        fontSize: 14, color: R.textSec, lineHeight: 1.65,
        padding: '4px 0', minWidth: 0,
      }}>
        {text.split('\n').map((line, i) => (
          <p key={i} style={{ margin: i > 0 ? '6px 0 0' : 0 }}>
            {line.includes('?')
              ? <><span style={{ color: R.text, fontWeight: 500 }}>{line.slice(0, line.lastIndexOf('?') + 1)}</span>{line.slice(line.lastIndexOf('?') + 1)}</>
              : line}
          </p>
        ))}
      </div>
    </div>
  );
}

// ── Chip de estatística — card informativo com hover ─────────────────────────
function StatChip({ icon, value, label }) {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
        padding: '10px 14px', borderRadius: 12,
        background: hovered ? R.chipHover : 'rgba(255,255,255,0.03)',
        border: `1px solid ${hovered ? R.chipHoverBdr : R.border}`,
        cursor: 'default', transition: 'all 0.18s ease',
        boxShadow: hovered ? '0 0 14px rgba(59,130,246,0.08)' : 'none',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 16, lineHeight: 1 }}>{icon}</div>
      <div style={{
        fontSize: 14, fontWeight: 700, color: R.text, letterSpacing: '-0.01em',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%',
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 10, color: R.textMut, textTransform: 'uppercase', letterSpacing: '0.07em',
        whiteSpace: 'nowrap',
      }}>{label}</div>
    </div>
  );
}

function StatRow({ data, wrap }) {
  const { recipeTime, recipeDiff, recipeServings, recipeType } = deriveMeta(data);
  const chips = [
    { icon: '⏱', value: recipeTime, label: 'Tempo total' },
    { icon: '📊', value: recipeDiff, label: 'Dificuldade' },
    { icon: '👥', value: recipeServings, label: 'Rendimento' },
    { icon: '🍽', value: recipeType, label: 'Tipo' },
  ];
  return (
    <div style={{ display: 'flex', gap: 10, ...(wrap ? { flexWrap: 'wrap' } : {}) }}>
      {chips.map(c => <StatChip key={c.label} {...c} />)}
    </div>
  );
}

// ── Badge RECEITA (pill ROXA, sobre a foto) ───────────────────────────────────
function RecipeBadge({ imgUrl }) {
  return (
    <div style={{
      position: 'absolute', top: 10, left: 12, zIndex: 3,
      padding: '4px 12px', borderRadius: 20,
      background: imgUrl ? 'rgba(139,92,246,0.9)' : 'rgba(139,92,246,0.15)',
      border: `1px solid ${R.purpleBdr}`,
      fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
      color: imgUrl ? '#fff' : R.purple,
      textTransform: 'uppercase',
      backdropFilter: imgUrl ? 'blur(8px)' : 'none',
      boxShadow: imgUrl ? '0 2px 12px rgba(139,92,246,0.3)' : 'none',
    }}>RECEITA</div>
  );
}

// ── Foto (real ou placeholder gradiente) ──────────────────────────────────────
// `fadeRight`: overlay que funde a borda direita da foto no fundo do card (desktop).
function HeroPhoto({ imgUrl, title, style, fadeRight }) {
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: 'linear-gradient(135deg, rgba(59,130,246,0.16), rgba(139,92,246,0.12))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      ...style,
    }}>
      {imgUrl ? (
        <img
          src={imgUrl} alt={title}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        /* Placeholder: gradiente intencional em vez de círculo com inicial */
        <div style={{
          width: '100%', height: '100%',
          background: 'linear-gradient(135deg, #0f1e36 0%, #1a2d50 50%, #0a1628 100%)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 8,
        }}>
          <div style={{ fontSize: 32, filter: 'grayscale(0.3)' }}>🍽</div>
          <div style={{ fontSize: 10, color: 'rgba(150,180,220,0.4)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Receita</div>
        </div>
      )}
      {fadeRight && imgUrl ? (
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: '30%',
          background: `linear-gradient(to right, transparent, ${R.card})`,
          pointerEvents: 'none',
        }} />
      ) : null}
      <RecipeBadge imgUrl={imgUrl} />
    </div>
  );
}

// ── Descrição do hero: real do backend ou auto-gerada pelo nome do prato ──────
// (nunca frases de conversa da Athena nem teasers terminados em ":")
function getHeroDesc(data) {
  const d = (data && data.description) || '';
  const valid = d.length > 10 && d.length < 200 && !d.endsWith(':')
    && !/aqui está|aqui esta|experimente|veja como|você pode|voce pode/i.test(d);
  return valid ? d : autoDesc(data && data.title);
}

// ── Hero card DESKTOP: foto 45% + texto, altura fixa 260px ────────────────────
function HeroCard({ data, imgUrl }) {
  const title = data.title || 'Receita';
  const titleFs = title.length > 38 ? 21 : title.length > 24 ? 26 : 30;
  const heroDesc = getHeroDesc(data);

  return (
    <div style={{
      ..._rCard, padding: 0, overflow: 'hidden', display: 'flex', height: 260,
      background: R.heroGrad,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      <HeroPhoto imgUrl={imgUrl} title={title} fadeRight style={{ flex: '0 0 45%', height: '100%' }} />

      <div style={{
        flex: 1, padding: '20px 24px',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        minWidth: 0,
      }}>
        <div>
          <div style={{
            fontSize: titleFs, fontWeight: 800, color: R.text, lineHeight: 1.2,
            letterSpacing: '-0.02em',
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {title}
          </div>
          {heroDesc ? (
            <div style={{
              fontSize: 14, color: R.textSec, lineHeight: 1.55, marginTop: 6,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {heroDesc}
            </div>
          ) : null}
        </div>

        <StatRow data={data} />
      </div>
    </div>
  );
}

// ── Stat chip MOBILE: coluna compacta — bordas laterais usadas no metrics row ─
function StatChipMobile({ icon, value, label, bordered }) {
  return (
    <div style={{
      flex: 1, minWidth: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 3, padding: '12px 4px',
      borderRight: bordered ? `1px solid ${R.border}` : 'none',
    }}>
      <div style={{ fontSize: 15, lineHeight: 1 }}>{icon}</div>
      <div style={{
        fontSize: 12, fontWeight: 700, color: R.text, lineHeight: 1.2,
        textAlign: 'center',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}>{value}</div>
      <div style={{
        fontSize: 9, color: R.textMut, textTransform: 'uppercase',
        letterSpacing: '0.05em', textAlign: 'center', lineHeight: 1.2,
      }}>{label}</div>
    </div>
  );
}

// ── Hero card MOBILE: texto esquerda + foto direita + metrics row integrado ───
function HeroCardMobile({ data, imgUrl }) {
  const title = data.title || 'Receita';
  const heroDesc = getHeroDesc(data);
  const { recipeTime, recipeDiff, recipeServings, recipeProfile } = deriveMeta(data);
  const titleFs = title.length > 32 ? 18 : title.length > 22 ? 21 : 24;
  const METRICS = [
    { icon: '⏱', value: recipeTime,     label: 'Tempo total', bordered: true  },
    { icon: '📊', value: recipeDiff,     label: 'Dificuldade', bordered: true  },
    { icon: '👥', value: recipeServings, label: 'Rendimento',  bordered: true  },
    { icon: '🌿', value: recipeProfile,  label: 'Perfil',      bordered: false },
  ];
  return (
    <div style={{
      ..._rCard, padding: 0, overflow: 'hidden',
      background: R.heroGrad,
      boxShadow: '0 4px 40px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.03) inset',
    }}>
      {/* Hero body: texto LEFT + foto RIGHT */}
      <div style={{ display: 'flex', minHeight: 202, position: 'relative' }}>
        {/* Coluna texto */}
        <div style={{
          flex: 1, minWidth: 0, padding: '20px 10px 18px 18px',
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: 0,
        }}>
          {/* Badge RECEITA */}
          <div style={{
            display: 'inline-block', alignSelf: 'flex-start',
            padding: '3px 10px', borderRadius: 20, marginBottom: 12,
            background: 'rgba(139,92,246,0.15)', border: `1px solid ${R.purpleBdr}`,
            fontSize: 10, fontWeight: 800, letterSpacing: '0.12em',
            color: R.purple, textTransform: 'uppercase',
          }}>RECEITA</div>
          {/* Título */}
          <div style={{
            fontSize: titleFs, fontWeight: 800, color: R.text,
            lineHeight: 1.24, letterSpacing: '-0.025em',
          }}>
            {title}
          </div>
          {/* Descrição */}
          {heroDesc ? (
            <div style={{
              fontSize: 12, color: 'rgba(220,234,255,0.62)', lineHeight: 1.55, marginTop: 10,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', fontWeight: 400,
            }}>
              {heroDesc}
            </div>
          ) : null}
        </div>

        {/* Coluna foto */}
        <div style={{ flexShrink: 0, width: '42%', position: 'relative', overflow: 'hidden' }}>
          {imgUrl ? (
            <>
              <img
                src={imgUrl} alt={title}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
              {/* Fade suave da borda esquerda — gradiente em 3 stops para transição mais natural */}
              <div style={{
                position: 'absolute', top: 0, left: 0, bottom: 0, width: '55%',
                background: `linear-gradient(to right, ${R.card} 0%, rgba(11,21,36,0.7) 50%, transparent 100%)`,
                pointerEvents: 'none',
              }} />
            </>
          ) : (
            <div style={{
              width: '100%', height: '100%', minHeight: 202,
              background: 'linear-gradient(135deg, #0f1e36 0%, #1a2d50 55%, #0a1628 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: 30, filter: 'grayscale(0.3)' }}>🍽</div>
            </div>
          )}
        </div>
      </div>

      {/* Metrics row — 4 chips com divisórias */}
      <div style={{
        borderTop: `1px solid ${R.border}`,
        display: 'flex',
        background: 'rgba(0,0,0,0.18)',
      }}>
        {METRICS.map(c => (
          <StatChipMobile key={c.label} icon={c.icon} value={c.value} label={c.label} bordered={c.bordered} />
        ))}
      </div>
    </div>
  );
}

// ── Ingredientes MOBILE: sem max-height, expansão natural ─────────────────────
function IngredientsCardMobile({ ingredients, tip }) {
  return (
    <div style={{ ..._rCard }}>
      <Label icon="🌿" label="Ingredientes" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(ingredients || []).map((item, i) => <IngItem key={i} text={item} />)}
      </div>
      {tip ? (
        <div style={{ marginTop: 14 }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(245,158,11,0.07) 0%, rgba(245,158,11,0.02) 100%)',
            border: `1px solid rgba(245,158,11,0.18)`,
            borderLeft: '2px solid rgba(245,158,11,0.45)',
            borderRadius: 10, padding: '16px 16px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
              <span style={{ fontSize: 14 }}>💡</span>
              <span style={{
                fontSize: 10, fontWeight: 800, color: R.amber,
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>DICA DA ATHENA</span>
            </div>
            <div style={{ fontSize: 13, color: R.textSec, lineHeight: 1.65 }}>{tip}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Modo de preparo MOBILE: timeline horizontal + expand ──────────────────────
function MobileStepsTimeline({ steps }) {
  const [expanded, setExpanded] = React.useState(false);
  const list = steps || [];
  return (
    <div style={{ ..._rCard }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 11, fontWeight: 800, color: R.textMut,
          textTransform: 'uppercase', letterSpacing: '0.09em' }}>
          <span style={{ fontSize: 13 }}>🍳</span>
          <span>Modo de Preparo</span>
        </div>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            fontSize: 11, color: R.accent,
            background: 'rgba(59,130,246,0.06)',
            border: `1px solid ${R.accentBdr}`,
            cursor: 'pointer', fontFamily: 'inherit',
            padding: '4px 10px', borderRadius: 20,
            outline: 'none', display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          {expanded ? 'Fechar ↑' : 'Ver todos os passos ∨'}
        </button>
      </div>

      {expanded ? (
        /* Lista vertical expandida */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {list.map((step, i) => <StepItem key={i} n={i + 1} text={step} />)}
        </div>
      ) : (
        /* Timeline horizontal — cada item desenha metade do conector dentro dos próprios limites */
        <div style={{
          display: 'flex',
          overflowX: 'auto', paddingBottom: 8,
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          {list.map((step, i) => (
            <div key={i} style={{
              flex: '0 0 80px', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 8,
            }}>
              {/* Linha conectora centrada no círculo de 32px */}
              <div style={{ position: 'relative', width: '100%', height: 32 }}>
                {/* metade esquerda (exceto primeiro) */}
                {i > 0 && (
                  <div style={{
                    position: 'absolute', left: 0, right: '50%', top: 15,
                    height: 1, background: 'rgba(99,102,241,0.25)',
                  }} />
                )}
                {/* metade direita (exceto último) */}
                {i < list.length - 1 && (
                  <div style={{
                    position: 'absolute', left: '50%', right: 0, top: 15,
                    height: 1, background: 'rgba(99,102,241,0.25)',
                  }} />
                )}
                {/* Círculo numerado */}
                <div style={{
                  position: 'absolute', top: 0, left: '50%',
                  transform: 'translateX(-50%)',
                  width: 32, height: 32, borderRadius: '50%',
                  background: 'rgba(99,102,241,0.82)',
                  boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700, color: '#fff', zIndex: 1,
                }}>{i + 1}</div>
              </div>
              <div style={{
                fontSize: 11, color: R.textMut, textAlign: 'center', lineHeight: 1.45,
                overflow: 'hidden', width: '100%', padding: '0 4px',
                display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              }}>
                {step.split(/[.,!]/)[0].slice(0, 42)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Bloco conversacional MOBILE: bubble + chips de ação ──────────────────────
const MOBILE_ACTION_CHIPS = [
  { id: 'airfryer',  icon: '🍳', label: 'Air Fryer'    },
  { id: 'fitness',   icon: '💪', label: 'Fitness'      },
  { id: 'lactose',   icon: '💧', label: 'Sem Lactose'  },
  { id: 'portions',  icon: '👥', label: 'Mais porções' },
];

function MobileConversationalBlock({ afterText, onAction }) {
  const [hovChip, setHovChip] = React.useState(null);
  const text = afterText
    || 'Essa receita combina muito bem com um jantar leve e saudável.\nO que você acha? Gostaria que eu adaptasse para alguma opção?';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <AfterTextBubble text={text} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {MOBILE_ACTION_CHIPS.map(c => (
          <button
            key={c.id}
            onClick={() => onAction && onAction(`follow:${c.id}`)}
            onMouseEnter={() => setHovChip(c.id)}
            onMouseLeave={() => setHovChip(null)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              height: 36, padding: '0 16px', borderRadius: 20,
              background: hovChip === c.id ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${hovChip === c.id ? R.accentBdr : R.border}`,
              color: hovChip === c.id ? R.accent : R.textSec,
              fontSize: 13, cursor: 'pointer',
              transition: 'all 0.15s ease', outline: 'none',
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}
          >
            <span style={{ fontSize: 13 }}>{c.icon}</span>
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Item de ingrediente — hover verde suave ───────────────────────────────────
function IngItem({ text }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        padding: '5px 6px', borderRadius: 8,
        background: hov ? R.greenHover : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: R.greenSoft, border: `1px solid ${R.greenBdr}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 2,
        fontSize: 10, color: R.green, fontWeight: 700,
      }}>✓</div>
      <span style={{ fontSize: 13.5, color: R.textSec, lineHeight: 1.5 }}>{text}</span>
    </div>
  );
}

// ── Item de passo — número maior, hover índigo suave ──────────────────────────
function StepItem({ n, text }) {
  const [hov, setHov] = React.useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '5px 6px', borderRadius: 8,
        background: hov ? 'rgba(99,102,241,0.05)' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        background: 'rgba(99,102,241,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, marginTop: 1,
        fontSize: 12.5, fontWeight: 700, color: '#fff',
      }}>{n}</div>
      <span style={{ fontSize: 14, color: R.textSec, lineHeight: 1.6 }}>{text}</span>
    </div>
  );
}

// ── Card Ingredientes (+ Dica da Athena) ──────────────────────────────────────
function IngredientsCard({ ingredients, tip }) {
  return (
    <div style={{ ..._rCard, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Label icon="🌿" label="Ingredientes" />
      <div style={{
        maxHeight: 280, overflowY: 'auto', paddingRight: 4,
        display: 'flex', flexDirection: 'column', gap: 2,
        scrollbarWidth: 'thin', scrollbarColor: `${R.border} transparent`,
      }}>
        {(ingredients || []).map((item, i) => <IngItem key={i} text={item} />)}
      </div>

      {tip ? (
        <div style={{ marginTop: 'auto', paddingTop: 10 }}>
          <div style={{
            background: R.tipGrad,
            border: `1px solid ${R.amberBdr}`,
            borderLeft: '3px solid rgba(245,158,11,0.7)',
            borderRadius: 10,
            padding: '12px 14px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
              <span style={{ fontSize: 15 }}>💡</span>
              <span style={{
                fontSize: 11, fontWeight: 800, color: R.amber,
                textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>DICA DA ATHENA</span>
            </div>
            <div style={{ fontSize: 13, color: R.textSec, lineHeight: 1.6 }}>{tip}</div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Card Modo de Preparo ──────────────────────────────────────────────────────
function StepsCard({ steps }) {
  return (
    <div style={{ ..._rCard, flex: 1, minWidth: 0 }}>
      <Label icon="🍳" label="Modo de Preparo" />
      <div style={{
        maxHeight: 320, overflowY: 'auto', paddingRight: 4,
        display: 'flex', flexDirection: 'column', gap: 4,
        scrollbarWidth: 'thin', scrollbarColor: `${R.border} transparent`,
      }}>
        {(steps || []).map((step, i) => <StepItem key={i} n={i + 1} text={step} />)}
      </div>
    </div>
  );
}

// ── Sidebar: Resumo Rápido — fallbacks inteligentes, nunca mostra "—" ─────────
function SummaryCard({ data }) {
  const { recipeTime, recipeDiff, recipeServings, recipeType, recipeProfile } = deriveMeta(data);
  const rows = [
    { icon: '⏱', k: 'Tempo total', v: recipeTime },
    { icon: '📊', k: 'Dificuldade', v: recipeDiff },
    { icon: '👥', k: 'Rendimento', v: recipeServings },
    { icon: '🍽', k: 'Tipo', v: recipeType },
    { icon: '🥗', k: 'Perfil', v: recipeProfile },
  ].filter(r => {
    if (!r.v || !String(r.v).trim()) return false;
    // Perfil: só mostra se não duplicar o Tipo
    if (r.k === 'Perfil' && recipeProfile === recipeType) return false;
    return true;
  });

  return (
    <div style={_rCard}>
      <Label icon="📋" label="Resumo Rápido" />
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map(({ icon, k, v }, i) => (
          <div key={k} style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: 8,
            padding: '7px 0',
            borderBottom: i < rows.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <span style={{ fontSize: 12, opacity: 0.7 }}>{icon}</span>
              <span style={{ fontSize: 12, color: R.textMut }}>{k}</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 700, color: R.text, textAlign: 'right', minWidth: 0 }}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Sidebar: Deseja adaptar? ──────────────────────────────────────────────────
const ADAPT_ICONS = {
  'Low Carb': '🥗', 'Fitness': '💪', 'Sem Lactose': '🥛',
  'Vegetariana': '🌿', 'Mais Proteica': '🔥',
};
const DEFAULT_ADAPTATIONS = ['Low Carb', 'Fitness', 'Sem Lactose', 'Vegetariana', 'Mais Proteica'];

function AdaptPill({ label, icon, onClick }) {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: '5px 12px', borderRadius: 16,
        background: hov ? 'rgba(59,130,246,0.1)' : 'transparent',
        border: `1px solid ${hov ? R.accentBdr : R.border}`,
        color: hov ? R.accent : R.textSec,
        fontSize: 12, cursor: 'pointer',
        transition: 'all 0.15s ease', outline: 'none',
        display: 'flex', alignItems: 'center', gap: 5,
        fontFamily: 'inherit',
      }}
    >
      {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
      {label}
    </button>
  );
}

function AdaptCard({ data, onAction }) {
  const adaptations = (data.adaptations && data.adaptations.length)
    ? data.adaptations
    : DEFAULT_ADAPTATIONS;
  return (
    <div style={_rCard}>
      <Label icon="🔄" label="Deseja adaptar?" />
      <div style={{ fontSize: 11.5, color: R.textMut, marginBottom: 8, lineHeight: 1.4 }}>
        Posso adaptar essa receita para:
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {adaptations.map(opt => (
          <AdaptPill
            key={opt}
            label={opt}
            icon={ADAPT_ICONS[opt]}
            onClick={() => onAction && onAction(`adapt:${opt}`)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Sidebar: Ações ────────────────────────────────────────────────────────────
const ACTION_BUTTONS = [
  { id: 'save',  icon: '🔖',  label: 'Salvar receita'  },
  { id: 'copy',  icon: '📋',  label: 'Copiar receita'  },
  { id: 'adapt', icon: '🔄',  label: 'Adaptar receita' },
  { id: 'image', icon: '🖼️', label: 'Gerar imagem'    },
  { id: 'share', icon: '🔗',  label: 'Compartilhar'    },
];

function ActionBtn({ icon, label, onClick }) {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', padding: '9px 13px', borderRadius: 9,
        background: hov ? R.actionHover : 'transparent',
        border: `1px solid ${hov ? 'rgba(255,255,255,0.1)' : R.border}`,
        color: R.textSec, fontSize: 13, cursor: 'pointer',
        transition: 'all 0.15s ease',
        outline: 'none', textAlign: 'left',
        fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span>{label}</span>
      </div>
      <span style={{ fontSize: 11, color: R.textMut, opacity: hov ? 1 : 0.5, transition: 'opacity 0.15s' }}>›</span>
    </button>
  );
}

function ActionsCard({ onAction }) {
  return (
    <div style={_rCard}>
      <Label icon="⚡" label="Ações" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {ACTION_BUTTONS.map(a => (
          <ActionBtn
            key={a.id}
            icon={a.icon}
            label={a.label}
            onClick={() => onAction && onAction(a.id)}
          />
        ))}
      </div>
    </div>
  );
}

// ── Follow chips (abaixo do after_text) ───────────────────────────────────────
const FOLLOW_CHIPS = [
  { id: 'airfryer',    icon: '🍳', label: 'Versão Air Fryer' },
  { id: 'fitness',     icon: '💪', label: 'Versão Fitness'   },
  { id: 'lactose',     icon: '💧', label: 'Sem Lactose'      },
  { id: 'portions',    icon: '👥', label: 'Mais porções'     },
  { id: 'vegetariana', icon: '🌿', label: 'Vegetariana'      },
];

function FollowChip({ icon, label, onClick }) {
  const [hov, setHov] = React.useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '7px 14px', borderRadius: 20,
        background: hov ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${hov ? R.accentBdr : R.border}`,
        color: hov ? R.accent : R.textSec,
        fontSize: 12.5, cursor: 'pointer',
        transition: 'all 0.18s ease', outline: 'none',
        fontFamily: 'inherit',
      }}
    >
      <span style={{ fontSize: 13 }}>{icon}</span>
      {label}
    </button>
  );
}

function FollowChips({ onAction }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {FOLLOW_CHIPS.map(c => (
        <FollowChip
          key={c.id}
          icon={c.icon}
          label={c.label}
          onClick={() => onAction && onAction(`follow:${c.id}`)}
        />
      ))}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
export function RecipeShowcaseRenderer({ data, onAction }) {
  const isMobile = useIsMobile(768);
  const [imgUrl, setImgUrl] = React.useState(null);
  const title = data?.title || '';
  // Resolution order: backend hero_image → legacy data.image → TheMealDB direct (last resort for old DB records)
  const heroImage = data?.hero_image || null;
  const image = heroImage?.desktop_url || data?.image || null;

  React.useEffect(() => {
    // 1. Backend already resolved — use it directly
    if (image) { setImgUrl(image); return; }
    // 2. Last resort: old DB records without hero_image → direct TheMealDB call
    const _ptEn = {
      frango: 'chicken', galinha: 'chicken', peru: 'turkey', pato: 'duck',
      carne: 'beef', bife: 'beef', picanha: 'beef', costela: 'beef', cordeiro: 'lamb',
      porco: 'pork', bacon: 'bacon',
      peixe: 'fish', salmão: 'salmon', atum: 'tuna', bacalhau: 'codfish', camarão: 'shrimp', tilapia: 'tilapia',
      macarrão: 'pasta', espaguete: 'spaghetti', lasanha: 'lasagna', pizza: 'pizza',
      bolo: 'cake', torta: 'pie', pudim: 'pudding', sorvete: 'icecream', brownie: 'brownie',
      arroz: 'rice', feijão: 'beans', lentilha: 'lentil', grão: 'chickpea',
      salada: 'salad', sopa: 'soup', caldo: 'broth',
      ovo: 'egg', queijo: 'cheese', tomate: 'tomato', cenoura: 'carrot',
      batata: 'potato', alho: 'garlic', cebola: 'onion',
    };
    const stopWords = new Set(['de', 'do', 'da', 'com', 'ao', 'e', 'em', 'para', 'uma', 'um', 'no', 'na', 'receita', 'os', 'as', 'dos', 'das', 'à', 'molho', 'grelhado', 'assado', 'cozido', 'frito']);
    const words = (title || '').toLowerCase().split(/\s+/)
      .filter(w => w.length > 2 && !stopWords.has(w));
    const translated = words.map(w => _ptEn[w] || null).filter(Boolean);
    const searchTerm = translated.length > 0 ? translated[0] : words.slice(0, 1).join('');
    if (!searchTerm || searchTerm.length < 3) return;
    let cancelled = false;
    fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(searchTerm)}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d?.meals?.[0]?.strMealThumb) {
          setImgUrl(d.meals[0].strMealThumb);
        } else if (translated.length > 1) {
          return fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(translated[1])}`)
            .then(r => r.json())
            .then(d2 => {
              if (!cancelled && d2?.meals?.[0]?.strMealThumb) setImgUrl(d2.meals[0].strMealThumb);
            });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [title, image]);

  if (!data || data.layout !== 'recipe') return null;

  // Intro = bolha conversacional da Athena ANTES do card. Não duplicar se for
  // idêntica à descrição já exibida no hero.
  const showIntro = data.intro_text && data.intro_text !== data.description;

  // ── Mobile: layout compacto 4 seções ────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: '100%' }}>
        {showIntro ? <ProseBlock text={data.intro_text} /> : null}
        <HeroCardMobile data={data} imgUrl={imgUrl} />
        <IngredientsCardMobile ingredients={data.ingredients} tip={data.tip} />
        <MobileStepsTimeline steps={data.steps} />
        <MobileConversationalBlock afterText={data.after_text} onAction={onAction} />
      </div>
    );
  }

  // ── Desktop: coluna principal (flex:1) + sidebar fixa 210px ────────────────
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', maxWidth: '100%' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {showIntro ? <ProseBlock text={data.intro_text} /> : null}
        <HeroCard data={data} imgUrl={imgUrl} />
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <IngredientsCard ingredients={data.ingredients} tip={data.tip} />
          <StepsCard steps={data.steps} />
        </div>
        {data.after_text ? <AfterTextBubble text={data.after_text} /> : null}
        <FollowChips onAction={onAction} />
      </div>

      <div style={{ width: 210, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SummaryCard data={data} />
        <AdaptCard data={data} onAction={onAction} />
        <ActionsCard onAction={onAction} />
      </div>
    </div>
  );
}

export default RecipeShowcaseRenderer;
